import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { expandFillRect, tileFillMap, type Cell, type Rect } from '@/utils/gridRange';
import type { GridSelection } from './useGridSelection';

/**
 * Excel-style drag-fill built on top of a {@link GridSelection}: grab the
 * selection's corner handle, drag to a target cell, and on release tile the
 * source values across the extended rectangle. Persistence is delegated to the
 * host via `apply`, and cell values are read through `readCell`, so this stays
 * ignorant of the row shape and the API.
 */
export interface GridFillOptions {
  selection: GridSelection;
  /** Map a column index to its persisted key. */
  colName: (ci: number) => string;
  /** Read a source cell's value (null when blank). */
  readCell: (ri: number, ci: number) => string | null;
  /** Persist the tiled `rowIndex → { column: value }` edits. */
  apply: (perRow: Map<number, Record<string, string | null>>) => void;
}

export interface GridFillHandle {
  /** True while the handle is being dragged. */
  isFilling: Ref<boolean>;
  /** The cell currently hovered during a drag. */
  fillTarget: Ref<Cell | null>;
  /** The rectangle the source will tile into, or null. */
  fillRect: ComputedRef<Rect | null>;
  /** Begin a fill from the current selection (on handle mousedown). */
  start(): void;
  /** Update the drag target as the pointer enters a cell. */
  hoverCell(ri: number, ci: number): void;
  /** Commit and reset the fill (on mouseup). No-op when not filling. */
  finish(): void;
}

export function useGridFillHandle(opts: GridFillOptions): GridFillHandle {
  const isFilling = ref(false);
  const fillTarget = ref<Cell | null>(null);

  const fillRect = computed<Rect | null>(() => {
    const sel = opts.selection.selRect.value;
    if (!isFilling.value || !fillTarget.value || !sel) return null;
    return expandFillRect(sel, fillTarget.value);
  });

  function start(): void {
    const sel = opts.selection.selRect.value;
    if (!sel) return;
    isFilling.value = true;
    fillTarget.value = { ri: sel.r2, ci: sel.c2 };
  }

  function hoverCell(ri: number, ci: number): void {
    if (isFilling.value) fillTarget.value = { ri, ci };
  }

  function commitFill(): void {
    const fill = fillRect.value;
    const sel = opts.selection.selRect.value;
    if (!fill || !sel) return;
    const perRow = tileFillMap(sel, fill, opts.readCell, opts.colName);
    // Grow the highlighted selection to cover what was just filled.
    opts.selection.setSelection({ ri: fill.r1, ci: fill.c1 }, { ri: fill.r2, ci: fill.c2 });
    opts.apply(perRow);
  }

  function finish(): void {
    if (!isFilling.value) return;
    commitFill(); // reads fillRect while isFilling is still true
    isFilling.value = false;
    fillTarget.value = null;
  }

  return { isFilling, fillTarget, fillRect, start, hoverCell, finish };
}
